/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable camelcase */

// node modules
import React, { Component, Fragment, Suspense } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import NoSsr from '@material-ui/core/NoSsr';
import withStyles from 'isomorphic-style-loader/withStyles';
import classNames from 'classnames';
import { throttle } from 'lodash';
import { Mutation } from '@apollo/client/react/components';

// app modules: actions
import { clearRecipe } from 'actions/homeconnect';
import { openModal } from 'actions/modals';
import { setInitialConversionData } from 'actions/conversion';
import { saveScrollPosition } from 'actions/pinnedPages';

// app modules: components
import Advert from 'components/Advert';
import AppearsIn from 'components/AppearsIn';
import Authors from 'components/Authors';
import Content from 'components/Content';
import ContentExtra from 'components/ContentExtra';
import ContentRaw from 'components/ContentRaw';
import GeneralActions from 'components/GeneralActions';
import GeneralHeader from 'components/GeneralHeader';
import HeadContent from 'components/HeadContent';
import InfoTile from 'components/InfoTile';
import InlinePaywall from 'components/InlinePaywall';
import RecipeImage from 'components/RecipeImage';
import RecipeOutput from 'components/RecipeOutput';
import StarRating from 'components/StarRating';
import withUserAccessLogger from 'components/UserAccessLogger';

// app modules: props
import AuthorsProps from 'components/Authors/AuthorsProps';
import NestedContentProps from 'components/NestedContent/NestedContentProps';
import RecipeImageProps from 'components/RecipeImage/RecipeImageProps';
import VideosProps from 'components/Videos/VideosProps';

// app modules: services
import { sendEvent } from 'services/analytics';
import assets from 'services/assets';
import { getImageSrc } from 'services/image';
import route from 'services/route';
import { getSharingText } from 'services/socialShare';
import { canShowRatingDialog, showRateUsPopUp } from 'services/rateUsOnStore';
import bookProgress from 'services/bookProgress';
import history from 'services/history';

import { CONTENT_TYPES, LS_LAST_TIME_POPUP_SHOWN } from 'constants/index';
import { MODALS } from 'constants/view';
import { GOOGLE_PLAY_REVIEW_INVITE } from 'constants/eventTypes';

import { LOG_RATE_POPUP_EVENTS, GET_RATE_POPUP_EVENTS } from './PopupLogQueries';

// assets
import CssRecipe from './Recipe.scss';

const BshAssistedCooking = React.lazy(() =>
  import(/* webpackChunkName: 'bsh-asssted-cooking' */ 'components/BshAssistedCooking'),
);

export class RecipeRenderComponent extends Content {
  static propTypes = {
    alacarte: PropTypes.bool,
    baseUrl: PropTypes.string.isRequired,
    canCopy: PropTypes.bool,
    canPrint: PropTypes.bool,
    logMutationFunction: PropTypes.func,
    checkingAccess: PropTypes.bool,
    clearRecipe: PropTypes.func.isRequired,
    setInitialConversionData: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    displayType: PropTypes.string.isRequired,
    enableSidebarAd: PropTypes.bool,
    enableVoice: PropTypes.bool,
    loading: PropTypes.bool,
    paywallError: PropTypes.string,
    previewOnly: PropTypes.bool,
    recipe: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.arrayOf(PropTypes.string),
      title_raw: PropTypes.arrayOf(PropTypes.string),
      subtitle: PropTypes.arrayOf(PropTypes.string),
      subtitle_lang: PropTypes.arrayOf(PropTypes.string),
      conversion: PropTypes.arrayOf(PropTypes.string),
      subtitle_raw: PropTypes.arrayOf(PropTypes.string),
      authors: AuthorsProps.authors,
      book: PropTypes.shape({
        signature_: PropTypes.string,
        isbn: PropTypes.string,
        title: PropTypes.string,
        title_short: PropTypes.string,
        image_name: PropTypes.string,
        image_permission: PropTypes.bool,
        image_credit: PropTypes.string,
        publication_date: PropTypes.string,
        ranking_recommendations: PropTypes.number,
        recipe_copyright_line: PropTypes.string,
        licensor: PropTypes.string,
        indexable: PropTypes.bool,
        licensed: PropTypes.bool,
        authors: AuthorsProps.authors,
      }),
      chapter: PropTypes.number,
      content_raw: PropTypes.arrayOf(PropTypes.string),
      intro_raw: PropTypes.string,
      ingredients: PropTypes.arrayOf(PropTypes.string),
      ingredients_raw: PropTypes.arrayOf(PropTypes.string),
      ingredients_list_units: PropTypes.arrayOf(PropTypes.string),
      procedures_raw: PropTypes.arrayOf(PropTypes.string),
      tools_raw: PropTypes.string,
      instagram_post_url: PropTypes.string,
      instagram_thumbnail_url: PropTypes.string,
      instagram_author_name: PropTypes.string,
      graphics: RecipeImageProps.graphics,
      nested_content: NestedContentProps.content,
      parentsNestedContents: NestedContentProps.content,
      parents: NestedContentProps.content,
      videos: VideosProps.videos,
      skill: PropTypes.string,
      output: PropTypes.arrayOf(
        PropTypes.shape({
          type: PropTypes.string,
          text: PropTypes.string,
          measure: PropTypes.arrayOf(
            PropTypes.shape({
              quantity: PropTypes.shape({
                type: PropTypes.string,
                value: PropTypes.string,
              }),
              unit: PropTypes.shape({
                system: PropTypes.string,
                type: PropTypes.string,
                value: PropTypes.string,
              }),
            }),
          ),
        }),
      ),
      ratings: PropTypes.shape({
        count: PropTypes.number,
        average: PropTypes.number,
      }),
      cook_time: PropTypes.number,
      prep_time: PropTypes.number,
      recipe_time: PropTypes.number,
    }).isRequired,
    refreshAccessPolicy: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    user: PropTypes.object,
    pinnedPages: PropTypes.shape({}),
    saveScrollPosition: PropTypes.func,
    userAuthorId: PropTypes.string,
    userId: PropTypes.number,
    userEmail: PropTypes.string,
    nCollections: PropTypes.number,
    positiveFeedbackCount: PropTypes.number,
    usingAdminAccess: PropTypes.bool,
  }; // eslint-disable-next-line react/forbid-prop-types

  static defaultProps = {
    alacarte: false,
    canCopy: true,
    canPrint: true,
    loading: false,
    checkingAccess: false,
    userAuthorId: null,
    userId: null,
    userEmail: null,
    user: null,
    refreshAccessPolicy: () => {},
    logMutationFunction: () => {},
    paywallError: null,
    openModal: () => {},
    previewOnly: false,
    enableVoice: false,
    enableSidebarAd: false,
    usingAdminAccess: false,
    pinnedPages: {},
    saveScrollPosition: () => {},
  };

  rateUsTimer = null;

  state = {
    modal: null,
    platformId: typeof window !== 'undefined' && window.cordova ? window.cordova.platformId : 'web',
    currentUserComment: null,
  };

  addReviewEvent(eventType) {
    const { userId, logMutationFunction } = this.props;

    const variables = { user_id: userId, event_type: eventType };

    logMutationFunction({ variables });
  }

  scrollToPinnedPosition(currentPinnedRecipe) {
    if (!currentPinnedRecipe) {
      console.warn('can not scroll to non-existent pinned recipe = ', currentPinnedRecipe);
      return;
    }
    const { scrollPosition } = currentPinnedRecipe;

    if (!scrollPosition) {
      return;
    }

    window.scrollTo({
      top: scrollPosition.y,
      left: scrollPosition.x,
      behavior: 'smooth',
    });
  }

  getCurrentPinnedRecipe() {
    const { pinnedPages } = this.props;
    const { recipe } = this.props;
    const generatedPathname = `/recipe/${recipe.id}`;

    const pinnedRecipeEntry = Object.entries(pinnedPages).find(entry =>
      entry[0].includes(generatedPathname),
    );

    if (!pinnedRecipeEntry || !history.location.pathname.includes(pinnedRecipeEntry[0])) {
      return null;
    }

    return {
      pathname: pinnedRecipeEntry[0],
      ...pinnedRecipeEntry[1],
    };
  }

  handleUserScrollStart = () => {
    this.isUserScroll = true;
    // Reset the flag after a delay to handle programmatic scrolls that might follow
    setTimeout(() => {
      this.isUserScroll = false;
    }, 300);
  };

  addUserScrollListeners() {
    window.addEventListener('wheel', this.handleUserScrollStart);
    window.addEventListener('touchstart', this.handleUserScrollStart);
    window.addEventListener('keydown', this.handleUserScrollStart);
  }

  removeUserScrollListeners() {
    window.removeEventListener('wheel', this.handleUserScrollStart);
    window.removeEventListener('touchstart', this.handleUserScrollStart);
    window.removeEventListener('keydown', this.handleUserScrollStart);
  }

  handleScroll = throttle(() => {
    const currentPinnedRecipe = this.getCurrentPinnedRecipe();

    if (!currentPinnedRecipe) {
      console.warn('no current recipe');
      return;
    }

    if (!this.isUserScroll) {
      console.warn('not a user scroll');
      return;
    }

    const scrollPosition = {
      x: window.scrollX,
      y: window.scrollY,
    };

    this.props.saveScrollPosition(currentPinnedRecipe.pathname, scrollPosition);
  }, 250);

  initHandlePinnedRecipesScroll() {
    const currentPinnedRecipe = this.getCurrentPinnedRecipe();

    if (currentPinnedRecipe && !this.props.loading) {
      this.scrollToPinnedPosition(currentPinnedRecipe);
    }

    this.addUserScrollListeners();
    window.addEventListener('scroll', this.handleScroll);
  }

  componentDidMount() {
    const { baseUrl, previewOnly, nCollections, positiveFeedbackCount } = this.props;
    const { id, title, book, chapter } = this.props.recipe;
    const { platformId } = this.state;

    if (!previewOnly) {
      if (window.intercom) {
        const url = `${baseUrl}${route.content(id, title)}`;
        window.intercom.logEvent('recipe-view', { url });
      }
      sendEvent({
        event: 'Content.Recipe.View',
        id,
        title: title.join(' '),
        book: book.title,
        chapter,
      });
      if (window && window.plugins && window.plugins.insomnia) {
        window.plugins.insomnia.keepAwake();
      }
    }

    // HANDLING PINNED RECIPES SCROLL RETAINING
    this.initHandlePinnedRecipesScroll();

    if (this.rateUsTimer || platformId === 'web') {
      return;
    }

    const areConditionsMet = canShowRatingDialog();

    if ((nCollections >= 10 || positiveFeedbackCount > 1) && areConditionsMet) {
      this.rateUsTimer = setTimeout(() => {
        if (platformId === 'android') {
          showRateUsPopUp().catch();

          this.addReviewEvent(GOOGLE_PLAY_REVIEW_INVITE);
          localStorage.setItem(LS_LAST_TIME_POPUP_SHOWN, new Date().toISOString());
        } else {
          const els = document.getElementsByClassName('rate-us-modal');

          if (els && els.length) {
            return;
          }

          import('components/RateUs/RateUs.jsx' /* webpackChunkName: "rate-us", webpackMode: "lazy" */).then(
            module => {
              this.setState({
                modal: module.default,
              });
              this.props.openModal({
                name: MODALS.RATE_US,
              });

              clearTimeout(this.rateUsTimer);
            },
          );
        }
      }, 15000);
    }
  }

  componentDidUpdate(prevProps) {
    const { recipe, user, checkingAccess, loading } = this.props;
    const {
      id,
      ingredients_raw,
      ingredients_list_units,
      openAIPrompts,
      unitConversionEnabled,
      book,
    } = recipe;

    if (
      !loading &&
      !checkingAccess &&
      openAIPrompts &&
      ingredients_raw !== null &&
      (recipe.ingredients_raw !== prevProps.recipe.ingredients_raw ||
        user?.permissions !== prevProps.user?.permissions)
    ) {
      const allowedToConvertRecipe = user?.permissions?.includes('feature:conversions'); // rename to canSeeConversionSlider
      const allowedToEditConversion = user?.permissions?.includes('conversions:edit');

      this.props.setInitialConversionData({
        openAIPrompts,
        allowedToConvertRecipe,
        allowedToEditConversion,
        ingredients_raw,
        ingredients_list_units,
        id,
        contentId: id,
        unitConversionEnabled,
      });
    }

    bookProgress.saveBookProgress(book.isbn, user.id, id);

    const currentPinnedRecipe = this.getCurrentPinnedRecipe();

    if (!this.props.loading && currentPinnedRecipe && !this.isUserScroll) {
      this.scrollToPinnedPosition(currentPinnedRecipe);
    }
  }

  componentWillUnmount() {
    const { previewOnly } = this.props;
    if (!previewOnly && window && window.plugins && window.plugins.insomnia) {
      window.plugins.insomnia.allowSleepAgain();
    }
    this.props.clearRecipe({});

    if (this.rateUsTimer) {
      clearTimeout(this.rateUsTimer);
    }

    this.removeUserScrollListeners();
    window.removeEventListener('scroll', this.handleScroll);
  }

  // eslint-disable-next-line class-methods-use-this
  formatRecipeTime(minutes) {
    let value;
    if (minutes < 120) {
      value = Math.ceil(minutes / 5) * 5;
    } else if (minutes < 300) {
      value = Math.ceil(minutes / 30) * 30;
    } else {
      value = Math.ceil(minutes / 60) * 60;
    }
    if (value < 60) {
      return `${value} min`;
    }
    const h = Math.floor(value / 60);
    const m = value % 60;
    if (!m) {
      return `${h} hr`;
    }
    return `${h} hr ${m}`;
  }

  renderPreparationInfo() {
    const { output, skill, recipe_time, is_skeleton } = this.props.recipe;
    const align = output.length > 2 ? 'top' : 'center';
    const content = {
      skill: skill ? (
        <li className="recipe__preparation">
          <InfoTile title="Difficulty" value={skill} align={align} />
        </li>
      ) : null,
      output:
        output.length === 0 ? null : (
          <li className="recipe__preparation">
            <RecipeOutput output={output} align={align} />
          </li>
        ),
      recipe_time: recipe_time ? (
        <li className="recipe__preparation">
          <InfoTile title="Ready in" value={this.formatRecipeTime(recipe_time)} align={align} />
        </li>
      ) : null,
      skeleton: is_skeleton ? (
        <li className="recipe__preparation">
          <Skeleton width="120px" height="100px" />
        </li>
      ) : null,
    };
    if (
      is_skeleton ||
      content.skill !== null ||
      content.output !== null ||
      content.recipe_time !== null
    ) {
      return (
        <Fragment>
          <div className="heading margin-bottom-10">Preparation info</div>
          <ul className="recipe__preparations">
            {content.output}
            <ul className="recipe__preparations-inner">
              {content.skill || content.skeleton}
              {content.recipe_time}
            </ul>
          </ul>
        </Fragment>
      );
    }

    return null;
  }

  render() {
    const {
      alacarte,
      bshAssistedCookingEnabled,
      canPrint,
      canCopy,
      checkingAccess,
      enableSidebarAd,
      enableVoice,
      forceSsr,
      isInitialPage,
      loading,
      paywallError,
      previewOnly,
      refreshAccessPolicy,
      userAuthorId,
      usingAdminAccess,
      conversion,
      isShowingOriginalRecipe,
    } = this.props;
    const {
      alexa_link,
      appliance_adjustments,
      authors,
      book,
      categories,
      content_raw,
      graphics,
      hash,
      id,
      ingredients_raw,
      instagram_author_name,
      instagram_html,
      instagram_post_url,
      instagram_thumbnail_height,
      instagram_thumbnail_url,
      instagram_thumbnail_width,
      instagram_width,
      intro_raw,
      is_preview,
      is_skeleton,
      is_variation,
      nested_content,
      parentsNestedContents,
      next,
      parents,
      prev,
      primary_image,
      procedures_raw,
      public_metadata,
      ratings,
      subtitle_raw,
      subtitle_lang,
      subtitle,
      title_raw,
      title,
      tools_raw,
      title_with_prefix,
      videos,
    } = this.props.recipe;

    const toRenderAuthors = authors?.length > 0 ? authors : book.authors;
    const toRenderTitle = title_with_prefix ? title_with_prefix.join(' ') : null;
    const toRenderSubtitle = subtitle ? subtitle.join(' ') : null;

    const { image_name } = this.props.recipe.book;
    const COVER_IMAGE_SIZE = 315;
    const bookCoverUrl = assets.coverUrl(image_name, {
      resize: 'fit-in',
      width: COVER_IMAGE_SIZE,
      height: COVER_IMAGE_SIZE,
      webp: false,
    });

    const sharingImageUrl =
      getImageSrc(primary_image, 'share', size => size.w >= 1000 || size.h >= 1000) ||
      getImageSrc(
        primary_image,
        'share',
        size => size.w >= 100 && size.w <= 2000 && size.h <= 2000,
        { searchHighToLow: true },
      );
    const canonicalUrl = route.content(id, title);

    const textToShare = getSharingText(toRenderTitle, book, toRenderAuthors);
    const abovePaywall = (
      <React.Fragment>
        {this.state.modal ? React.createElement(this.state.modal) : null}

        <Advert
          className="margin-bottom-20"
          slotName="leaderboard-header"
          format="leaderboard"
          fullWidthResponsive={false}
          ezoicPlaceholderId={101}
        />
        {previewOnly ? null : (
          <HeadContent
            contents={{
              content: bookCoverUrl,
              width: COVER_IMAGE_SIZE,
              height: COVER_IMAGE_SIZE,
              structuredData: public_metadata,
              url: canonicalUrl,
            }}
          />
        )}
        <GeneralHeader
          leftComponent={<Authors authors={toRenderAuthors} />}
          title={toRenderTitle}
          titleRaw={title_raw}
          subtitle={toRenderSubtitle}
          subtitleLang={subtitle_lang}
          subtitleRaw={subtitle_raw}
          rightComponent={
            <NoSsr
              fallback={
                <StarRating
                  contentId={id}
                  contentHash={hash}
                  averageRating={0}
                  noText
                  currentUserComment={this.state.currentUserComment}
                  className="recipe__stars"
                  readOnly
                />
              }
            >
              <StarRating
                contentId={id}
                contentHash={hash}
                averageRating={ratings.average}
                count={ratings.count}
                currentUserComment={this.state.currentUserComment}
                className="recipe__stars"
                readOnly={previewOnly}
              />
            </NoSsr>
          }
        />
        <RecipeImage
          instagramThumbnailUrl={instagram_thumbnail_url}
          instagramPostUrl={instagram_post_url}
          instagramAuthor={instagram_author_name}
          instagramThumbnailWidth={instagram_thumbnail_width}
          instagramThumbnailHeight={instagram_thumbnail_height}
          instagramHtml={instagram_html}
          instagramWidth={instagram_width}
          graphics={graphics}
          isInitialPage={isInitialPage}
          enableLightbox={!is_preview}
          image={
            primary_image && {
              ...primary_image,
              credit: !primary_image.credit
                ? Content.getImageCredit(primary_image, book)
                : primary_image,
            }
          }
          contentRaw={content_raw}
        />
        <div className="recipe__columns recipe__tools-and-social">
          <section className="recipe__toolbox">
            <GeneralActions
              ingredientsRaw={ingredients_raw}
              authors={toRenderAuthors}
              loading={is_skeleton}
              canPrint={canPrint}
              contentId={id}
              contentType={CONTENT_TYPES.RECIPE}
              hash={hash}
              canPinThePage
              recipe={this.props.recipe}
              shareImage={sharingImageUrl}
              title={toRenderTitle}
              text={textToShare}
              previewOnly={previewOnly}
              enableVoice={enableVoice}
              voiceLinks={alexa_link ? [{ provider: 'alexa', url: alexa_link }] : []}
            />
          </section>
        </div>
        <div className="recipe__columns">
          <section className="recipe__column margin-bottom-20">
            {this.renderPreparationInfo()}
          </section>

          <section className="recipe__column margin-bottom-40">
            <AppearsIn book={book} id={id} loading={is_skeleton} />
          </section>
        </div>
        {bshAssistedCookingEnabled ? (
          <NoSsr defer>
            <Suspense fallback={null}>
              <BshAssistedCooking recipeId={id} />
            </Suspense>
          </NoSsr>
        ) : null}
      </React.Fragment>
    );

    let ingredientsToShow = null;

    if (conversion && conversion.id === id) {
      ingredientsToShow = conversion.ingredientsToShow;
    }

    let rawIngredients = null;

    if (
      isShowingOriginalRecipe &&
      ingredients_raw &&
      ingredients_raw.length > 0 &&
      ingredients_raw.some(ingredient => ingredient.trim() !== '')
    ) {
      rawIngredients = ingredients_raw;
    }

    const behindPaywall = (
      <ContentRaw
        id={id}
        raw={{
          intro: intro_raw,
          ingredients: rawIngredients || ingredientsToShow,
          procedures: procedures_raw,
          tools: tools_raw,
        }}
        contents={content_raw}
        image={{
          credit: Content.getImageCredit(primary_image, book),
          primary: primary_image?.src,
          permission: book.image_permission,
          type: primary_image?.type,
        }}
        instagramHtml={instagram_html}
        instagramWidth={instagram_width}
        book={book}
        isPreview={previewOnly || is_preview}
        graphics={graphics}
        categories={categories}
        highlightAdjustments={
          Array.isArray(appliance_adjustments) && appliance_adjustments.length > 0
        }
        loading={is_skeleton}
      />
    );
    const contentExtra = (
      <ContentExtra
        authors={authors}
        book={book}
        forceSsr={forceSsr}
        hash={hash}
        hasPrimaryImage={
          primary_image &&
          (primary_image?.type === 'primary' || primary_image?.type === 'instagram')
        }
        id={id}
        isPreview={is_preview}
        isVariation={is_variation}
        nestedContent={nested_content}
        parentsNestedContents={parentsNestedContents}
        setUserRecipeReview={review => this.setState({ currentUserComment: review })}
        next={next}
        parents={parents}
        prev={prev}
        title={title}
        type="Recipe"
        userAuthorId={userAuthorId}
        videos={videos}
        withComments
      />
    );
    const afterPaywall =
      previewOnly || is_skeleton ? null : (
        <NoSsr defer fallback={forceSsr ? contentExtra : null}>
          {contentExtra}
        </NoSsr>
      );
    const contiguousContent = (
      <React.Fragment>
        {abovePaywall}
        <InlinePaywall
          alacarte={alacarte}
          error={paywallError}
          isbn={book.isbn}
          loading={loading || checkingAccess}
          refreshAccessPolicy={refreshAccessPolicy}
          sticky
          usingAdminAccess={usingAdminAccess}
          visible={is_preview}
        >
          {behindPaywall}
        </InlinePaywall>
        {afterPaywall}
      </React.Fragment>
    );
    const rootClasses = classNames('recipe', {
      'recipe--preview': is_preview,
      'recipe--no-copy': !canCopy,
      'recipe--no-print': !canPrint,
    });

    if (!enableSidebarAd) {
      return (
        <div className={rootClasses} data-cy={previewOnly ? 'content-preview' : 'recipe'}>
          {contiguousContent}
        </div>
      );
    }
    if (is_preview) {
      // Double track for advert - with paywall between runs
      return (
        <div className={rootClasses} data-cy={previewOnly ? 'content-preview' : 'recipe'}>
          <div className="recipe__outer-cols">
            <div className="recipe__content-col">{abovePaywall}</div>
            <div className="recipe__ad-col recipe__ad-col--extend-over-fog">
              <Advert
                slotName="floating-sidebar"
                format="vertical"
                hideOnMobile
                fullWidthResponsive={false}
                ezoicPlaceholderId={109}
                className="recipe__sidebar-ad1"
                lazy
              />
            </div>
          </div>
          <InlinePaywall
            alacarte={alacarte}
            error={paywallError}
            sticky
            visible={is_preview}
            loading={loading || checkingAccess}
            refreshAccessPolicy={refreshAccessPolicy}
            isbn={book.isbn}
            usingAdminAccess={usingAdminAccess}
          >
            <div className="recipe__outer-cols">
              <div className="recipe__content-col">{behindPaywall}</div>
              <div className="recipe__ad-col">
                <div className="recipe__sidebar-ad1" />
              </div>
            </div>
          </InlinePaywall>
          <div className="recipe__outer-cols">
            <div className="recipe__content-col">{afterPaywall}</div>
            <div className="recipe__ad-col">
              <Advert
                slotName="floating-sidebar"
                format="vertical"
                hideOnMobile
                fullWidthResponsive={false}
                ezoicPlaceholderId={110}
                className="recipe__sidebar-ad2"
                lazy
              />
            </div>
          </div>
        </div>
      );
    }
    // Single track for advert
    return (
      <div className={rootClasses} data-cy={previewOnly ? 'content-preview' : 'recipe'}>
        <div className="recipe__outer-cols">
          <div className="recipe__content-col">{contiguousContent}</div>
          <div className="recipe__ad-col">
            <Advert
              slotName="floating-sidebar"
              format="vertical"
              hideOnMobile
              fullWidthResponsive={false}
              ezoicPlaceholderId={109}
              className="recipe__sidebar-ad-single"
              lazy
            />
          </div>
        </div>
      </div>
    );
  }
}

export class RecipeComponent extends Component {
  static propTypes = { userId: PropTypes.number };
  static defaultProps = { userId: -1 };
  render() {
    return (
      <Mutation
        mutation={LOG_RATE_POPUP_EVENTS}
        refetchQueries={[
          {
            query: GET_RATE_POPUP_EVENTS,
            variables: { user_id: this.props.userId ? this.props.userId : -1 },
          },
        ]}
      >
        {mutateFunction => (
          <RecipeRenderComponent logMutationFunction={mutateFunction} {...this.props} />
        )}
      </Mutation>
    );
  }
}

const mapStateToProps = state => ({
  displayType: state.dimensions.displayType,
  queryString: state.router.search,
  baseUrl: state.config.baseUrl,
  userId: state.user.id,
  userEmail: state.user.email,
  userAuthorId: state.user.author_id,
  user: state.user,
  pinnedPages: state.pinnedPages,
  positiveFeedbackCount: state.user.positiveFeedbackCount,
  nCollections: Object.keys(state.user.collections).length,
  bshAssistedCookingEnabled: state.user.bsh_assisted_cooking_enabled,
  conversion: state.conversion,
  isShowingOriginalRecipe: state.conversion.isShowingOriginalRecipe,
});

const mapDispatchToProps = {
  clearRecipe,
  openModal,
  setInitialConversionData,
  saveScrollPosition,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssRecipe)(withUserAccessLogger(CONTENT_TYPES.RECIPE, RecipeComponent)));
